import {
  ChangeBlocksInputType,
  ChangeClassroomInputType,
  ChangeInstructorInputType,
  ChangeIntervalsInputType,
  Day,
  EditorChangesetInput,
  EditorOperation,
  Instructor,
  Maybe,
  Section,
  SessionCrudChangesetInput,
} from "@models/ISchema";
import { netUnassignedInstructorIds } from "@utils/unassignedInstructors";
import { BlockRanges, EditedSession, FormPageType } from "../context/formData.reducer";
import { savableSessionsToCreate } from "./context";
import { getOriginalSessionsById } from "./getOriginalSessionsById";

type ChangesetFormState = Pick<
  FormPageType,
  "savedSessions" | "sessionsToCreate" | "sessionsToDelete"
>;

const blocksByHours = (blocks: BlockRanges): ChangeBlocksInputType => {
  const day = blocks?.day?.toLowerCase()?.replace(/^\w/, (c: string) => c.toUpperCase());
  return {
    op: EditorOperation.Change,
    day: Day[day],
    startTime: blocks?.startTime,
    endTime: blocks?.endTime,
  };
};

const changeClassroom = (session: EditedSession): ChangeClassroomInputType => ({
  op: EditorOperation.Change,
  classroomIds: (session?.classrooms ?? []).map(classroom => classroom?.id),
});

const changeInstructor = (session: EditedSession): ChangeInstructorInputType => ({
  op: EditorOperation.Change,
  instructorIds: (session?.instructors ?? []).map(instructor => instructor?.id),
});

const changeIntervals = (session: EditedSession): ChangeIntervalsInputType => ({
  op: EditorOperation.Change,
  intervalIds: (session?.intervals ?? [])
    .filter(interval => interval?.checked ?? false)
    .map(interval => interval?.id),
  uniqueInterval: false,
});

const buildSessionChangeset = (session: EditedSession): EditorChangesetInput => {
  const blocks =
    session?.blocks?.selected === "hours"
      ? { changeBlocks: blocksByHours(session?.blocks) }
      : { noScheduleEventsCount: session?.blocks?.blocks };

  return {
    sessionId: session?.id,
    // Send the section id on updates too (not only on creation): the backend
    // VALIDATE_EXCEEDED_SCHEDULED_BLOCKS validation reads section_id from the
    // changeset, so a null here silently skips it for edited sessions (CHS-2785).
    sectionId: session?.section?.id ?? session?.session?.section?.id ?? null,
    ...blocks,
    changeClassroom: changeClassroom(session),
    changeInstructor: changeInstructor(session),
    changeIntervals: changeIntervals(session),
  };
};

/** The edited sessions that are sent as updates and creates; deleted sessions are left out. */
const sessionsToSave = (
  formState: ChangesetFormState,
): { updates: EditedSession[]; creates: EditedSession[] } => {
  const sessionsToDelete = formState?.sessionsToDelete ?? {};
  const { updates, creates } = (formState?.savedSessions ?? [])
    .filter(session => !Object.prototype.hasOwnProperty.call(sessionsToDelete, session?.id ?? "-"))
    .reduce<{ updates: EditedSession[]; creates: EditedSession[] }>(
      (acc, session) => {
        if (session?.isNew) acc.creates.push(session);
        else acc.updates.push(session);
        return acc;
      },
      { updates: [], creates: [] },
    );

  // Add the "cloned" sessions to the "creates" array
  creates.push(...savableSessionsToCreate(formState?.sessionsToCreate));

  return { updates, creates };
};

/**
 * Turns the form state of the traditional assignation editor into the changesets that
 * sessionsCrud and submitAssignmentProposal both accept. Pure and shared, so a direct save and a
 * proposal always send exactly the same changes.
 */
export const buildAssignationChangesets = (
  formState: ChangesetFormState,
): SessionCrudChangesetInput => {
  const { updates, creates } = sessionsToSave(formState);

  return {
    updates: updates.map(buildSessionChangeset),
    creates: creates.map(buildSessionChangeset),
    deletes: {
      sections: [],
      sessions: Object.keys(formState?.sessionsToDelete ?? {}),
    },
  };
};

const instructorIds = (instructors: Maybe<Instructor>[] | null | undefined) =>
  (instructors ?? []).map(instructor => instructor?.id);

/**
 * The instructors the traditional editor unassigned, for `unassignedInstructorIds` (CHS-2944).
 *
 * Every session sent to sessionsCrud is compared with the saved session it continues: an update
 * with itself and a fork with the session it was split from. The blocked part of a fork keeps the
 * original instructors, so an instructor removed from the future weeks only shows up on the fork.
 * New and cloned sessions continue none, and deleted sessions are not sent.
 */
export const buildUnassignedInstructorIds = (
  formState: ChangesetFormState,
  sections: Section[],
): string[] => {
  const originalSessionsById = getOriginalSessionsById(sections);
  const { updates, creates } = sessionsToSave(formState);

  return netUnassignedInstructorIds(
    [...updates, ...creates].map(session => {
      const originId = Object.keys(session?.forkedWeeksByOriginSessions ?? {})[0] ?? session?.id;

      return {
        originalIds: instructorIds(originalSessionsById[originId]?.instructors),
        // Read as `changeInstructor` sends them, so the list never disagrees with the changeset.
        finalIds: instructorIds(session?.instructors),
      };
    }),
  );
};

const bySessionId = (a: EditorChangesetInput, b: EditorChangesetInput) =>
  String(a?.sessionId).localeCompare(String(b?.sessionId));

const sortedIds = (ids: readonly unknown[] | null | undefined): string[] | null =>
  ids == null ? null : ids.map(String).sort();

/** The ids of a changeset are sets: removing a classroom and adding it back changes nothing. */
const withSortedIds = (changeset: EditorChangesetInput): EditorChangesetInput => ({
  ...changeset,
  ...(changeset?.changeClassroom && {
    changeClassroom: {
      ...changeset.changeClassroom,
      classroomIds: sortedIds(changeset.changeClassroom.classroomIds),
    },
  }),
  ...(changeset?.changeInstructor && {
    changeInstructor: {
      ...changeset.changeInstructor,
      instructorIds: sortedIds(changeset.changeInstructor.instructorIds),
    },
  }),
  ...(changeset?.changeIntervals && {
    changeIntervals: {
      ...changeset.changeIntervals,
      intervalIds: sortedIds(changeset.changeIntervals.intervalIds),
    },
  }),
});

const normalizedSessions = (changesets: EditorChangesetInput[] | null | undefined) =>
  (changesets ?? []).map(withSortedIds).sort(bySessionId);

/**
 * A stable text of some changesets, to tell whether the editor changed since a proposal was
 * applied. Order does not count, neither of the sessions (editing one can move it in the lists)
 * nor of the resources inside each one.
 */
export const changesetsFingerprint = (
  changesets: SessionCrudChangesetInput | null | undefined,
): string =>
  JSON.stringify({
    updates: normalizedSessions(changesets?.updates),
    creates: normalizedSessions(changesets?.creates),
    deletes: [...(changesets?.deletes?.sessions ?? [])].map(String).sort(),
  });
